import { InputHTMLAttributes, forwardRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { smoothTransition, shakeVariants } from "@/lib/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className, onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <motion.div
        className="w-full"
        animate={error && !prefersReducedMotion ? "shake" : undefined}
        variants={shakeVariants}
      >
        {label && (
          <label
            htmlFor={props.id}
            className={cn(
              "block text-[13px] font-medium mb-1.5 transition-colors duration-150",
              error
                ? "text-[var(--color-error)]"
                : isFocused
                  ? "text-[var(--color-accent-custom,var(--color-primary))]"
                  : "text-[var(--color-text-secondary)]"
            )}
          >
            {label}
            {props.required && <span className="text-[var(--color-error)] ml-0.5">*</span>}
          </label>
        )}
        <motion.div
          className="relative"
          animate={
            prefersReducedMotion
              ? {}
              : {
                  scale: isFocused ? 1.005 : 1,
                }
          }
          transition={smoothTransition}
        >
          <input
            ref={ref}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              "w-full h-[44px] px-4 text-[14px]",
              "bg-[var(--color-surface-primary)] border rounded-lg",
              "text-[var(--color-text-primary)]",
              "placeholder:text-[var(--color-text-placeholder)]",
              "transition-all duration-150",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              "disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed",
              error
                ? "border-[var(--color-error)] focus:ring-[var(--color-error)]/20 focus:border-[var(--color-error)]"
                : "border-[var(--color-surface-border)] focus:ring-[var(--color-accent-custom,var(--color-primary))]/20 focus:border-[var(--color-accent-custom,var(--color-primary))]",
              className
            )}
            {...props}
          />
          <AnimatePresence>
            {isFocused && !error && !prefersReducedMotion && (
              <motion.div
                className="absolute inset-0 rounded-lg pointer-events-none"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={smoothTransition}
                style={{
                  boxShadow: "var(--shadow-focus)",
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -5, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -5, height: 0 }}
              transition={smoothTransition}
              className="mt-1.5 text-[13px] text-[var(--color-error)] flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </motion.p>
          )}
          {helperText && !error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={smoothTransition}
              className="mt-1.5 text-[13px] text-[var(--color-text-muted)]"
            >
              {helperText}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

Input.displayName = "Input";
